import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { Menu, X } from "lucide-react";
import { API_URL, Button, BarreNavigation } from "./Dashboard";

const Todolist = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [taches, setTaches] = useState([]);
  const [nomNouvelleTache, setNomNouvelleTache] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Chargement de l'utilisateur et récupération des tâches de son pôle
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) {
      navigate("/");
      return;
    }
    setUser(storedUser);
    fetchTaches(storedUser.identifiant);
  }, [navigate]);

  // Récupération des tâches liées au pôle de l'utilisateur
  const fetchTaches = (identifiant) => {
    fetch(`${API_URL}/obtenirTaches.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ identifiant }),
    })
      .then((res) => res.json())
      .then((data) => {
        // S'assurer que data est un tableau
        if (Array.isArray(data)) {
          setTaches(data);
        } else {
          setTaches([]);
        }
      })
      .catch((err) => {
        console.error(err);
        setTaches([]);
      });
  };

  if (!user) return null;

  const seDeconnecter = () => {
    localStorage.removeItem("user");
    navigate("/");
  };

  const ajouterTache = () => {
    if (!nomNouvelleTache.trim()) return;

    fetch(`${API_URL}/ajouterTache.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nom: nomNouvelleTache, identifiant: user.identifiant }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setNomNouvelleTache("");
          fetchTaches(user.identifiant);
        } else alert(data.error || "Erreur lors de l'ajout");
      })
      .catch(console.error);
  };

  const modifierTache = (id, currentStatus) => {
    fetch(`${API_URL}/modifierTache.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, terminee: !currentStatus, identifiant: user.identifiant }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) fetchTaches(user.identifiant);
        else alert(data.error || "Erreur lors de la mise à jour");
      })
      .catch(console.error);
  };

  const supprimerTache = (id) => {
    fetch(`${API_URL}/supprimerTache.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, identifiant: user.identifiant }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) fetchTaches(user.identifiant);
        else alert(data.error || "Erreur lors de la suppression");
      })
      .catch(console.error);
  };

  return (
    <div className="flex min-h-screen bg-gray-50 relative overflow-x-hidden">
        
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div className={`
        fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-md transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
         <BarreNavigation 
            user={user} 
            deconnexion={seDeconnecter} 
            onNavigate={() => setIsMobileMenuOpen(false)}
         />
         <button 
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute top-4 right-4 lg:hidden text-gray-500"
        >
            <X size={24} />
        </button>
      </div>

      <main className="flex-1 p-4 lg:p-8 w-full">
        <header className="flex items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
             {/* Bouton burger */}
             <button 
                className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                onClick={() => setIsMobileMenuOpen(true)}
            >
                <Menu size={24} />
            </button>
            <h1 className="text-xl md:text-2xl font-bold text-gris-sombre">La to-do list de mon pôle</h1>
          </div>
          
          {/* Bouton deconnexion responsive */}
          <Button variant="outline" onClick={seDeconnecter} title="Se déconnecter">
             <span className="hidden sm:inline">Se déconnecter</span>
             <LucideIcons.LogOut size={16} className="sm:hidden" />
          </Button>
        </header>

        <ul className="space-y-2">
          {(!Array.isArray(taches) || taches.length === 0) && (
            <li className="text-gray-500 italic">Aucune tâche pour ton pôle.</li>
          )}

          {Array.isArray(taches) &&
            taches.map((task) => (
              <li
                key={task.id}
                className="flex items-center justify-between bg-white/50 p-3 rounded-xl shadow gap-3"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <button
                    onClick={() => modifierTache(task.id, Number(task.terminee) === 1)}
                    className={`w-6 h-6 rounded-full border-2 flex-shrink-0 ${
                      Number(task.terminee) === 1
                        ? "bg-yellow-400 border-yellow-500"
                        : "border-gray-400"
                    }`}
                  />
                  <span
                    className={`break-words min-w-0 ${
                      Number(task.terminee) === 1 ? "line-through text-gray-500" : "text-gray-800"
                    }`}
                  >
                    {task.nom}
                  </span>
                </div>
                <button
                  onClick={() => supprimerTache(task.id)}
                  className="p-2 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center flex-shrink-0"
                >
                  <LucideIcons.Trash2 size={16} />
                </button>
              </li>
            ))}

          {/* Champ pour ajouter une tâche */}
          <li className="flex items-center gap-3 mt-4">
            <input
              type="text"
              value={nomNouvelleTache}
              onChange={(e) => setNomNouvelleTache(e.target.value)}
              placeholder="Nouvelle tâche"
              className="flex-1 border rounded-lg px-3 py-2 min-w-0"
            />
            <button
              onClick={ajouterTache}
              className="p-2 rounded-full bg-green-600 hover:bg-green-700 text-white flex items-center justify-center flex-shrink-0"
            >
              <LucideIcons.Plus size={16} />
            </button>
          </li>
        </ul>
      </main>
    </div>
  );
};

export default Todolist;